import {
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import {DecimalPipe, isPlatformBrowser, NgOptimizedImage} from '@angular/common';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {of, Subject} from 'rxjs';
import {catchError, filter, map, takeUntil} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';

import {OrderContextStore} from '../../../store/order-context.store';
import {ConfigurationStore} from '../../../store/configuration.store';
import {TypeOrderEnum} from '../../../enum/type-order.enum';
import {ProductService} from '../../../services/product.service';
import {PromosService} from '../../../services/promos.service';
import {NotificationType} from '../../../enum/notification-type.enum';
import {
  DetailsProductMenusComponent
} from '../../menus/details-menus/details-product-menus/details-product-menus.component';
import {NotificationService} from '../../../notification/notification.service';
import {
  BundleCartItem,
  CartItem,
  CartItemType,
  CartService,
  ProductCartItem,
  PromotionCartItem
} from '../../../services/cart.service';
import {HideFloatingCartService} from '../../../services/hide-floating-cart.service';
import {OrderComponent} from '../../../order/order.component';
import {Meta, MetaDefinition, Title} from '@angular/platform-browser';
import {THEME_CONFIG} from '../../../../environments/tokens/theme.token';
import {TopNavbarComponent} from '../../../shared/top-navbar/top-navbar.component';
import {NavbarComponent} from '../../../shared/navbar/navbar.component';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {TenantService} from '../../../services/tenant.service';
import {AdvertisementService} from '../../../services/advertisement.service';
import {AuthenticationService} from '../../../services/authentication.service';
import {BundleService} from '../../../services/bundle.service';
import {AdvertisementFlowService} from '../../../services/advertisement-flow.service';
import {CustomerService} from '../../../services/customer.service';

@Component({
  selector: 'app-details-promos',
  templateUrl: './details-promos.component.html',
  styleUrl: './details-promos.component.scss',
  imports: [TopNavbarComponent, NavbarComponent, MatIcon, MatButton, DecimalPipe, NgOptimizedImage]
})
export class DetailsPromosComponent implements OnInit, OnDestroy {
  // Structural SSR Handlers
  private platformId = inject(PLATFORM_ID);
  private destroy$ = new Subject<void>();
  isBrowser = isPlatformBrowser(this.platformId);
  protected readonly themeConfig = inject(THEME_CONFIG)
  private adFlowService = inject(AdvertisementFlowService);
  // Core DI Dependencies
  private orderContextStore = inject(OrderContextStore);
  private configStore = inject(ConfigurationStore);
  private promosService = inject(PromosService); // Direct service interaction for SSR reliability
  private route = inject(ActivatedRoute);
  private productService = inject(ProductService);
  private dialog = inject(MatDialog);
  private notificationService = inject(NotificationService);
  private cartService = inject(CartService);
  private router = inject(Router);
  private metaService = inject(Meta)
  private titleService = inject(Title)
  readonly isSubmitting = signal<boolean>(false);
  private metaTags: HTMLMetaElement[] = []
  private tenantService = inject(TenantService);
  isMilano = false;
  private bundleService = inject(BundleService);
  // Layout View Containers
  @ViewChild('scrollableContent') scrollableContent!: ElementRef<HTMLDivElement>;

  // Context Signals
  selectedOrderType = this.orderContextStore.orderType;
  cityName = this.orderContextStore.cityName;
  zipCode = this.orderContextStore.zipcode;
  deliveryDelay = this.orderContextStore.deliveryDelay;
  minOrderAmounts = this.orderContextStore.minOrderAmounts;
  readonly restaurantAddress = this.configStore.address;
  hideFloatingCartService = inject(HideFloatingCartService);

  // Component Core States
  promoId!: string;
  promoName!: string;
  readonly isLoading = signal<boolean>(true);

  // WritableSignal ensures full backward compatibility with any template readings
  promoSignal: WritableSignal<any | null> = signal(null);

  // Customization Tracking Parameters
  selectedItemQuantities: { [ruleId: string]: { [productId: string]: number } } = {};
  customizedItems: { [productId: string]: { [index: number]: any } } = {};
  customizableItems: { [productId: string]: boolean } = {};
  productRequiredAdditions: { [productId: string]: boolean } = {};
  private fullProductsCache: { [productId: string]: any } = {};

  // Mobile Bottom Sheet Configurations
  isCartSheetOpen: boolean = false;
  touchStartY = 0;
  touchCurrentY = 0;
  sheetTransform = 'translateY(0px)';
  SWIPE_CLOSE_THRESHOLD = 120;
  private advertisementService = inject(AdvertisementService);
  private authenticationService = inject(AuthenticationService);
  private advertisementQueue: any[] = [];
  private fromMenuFlow = false;
  private readonly SEEN_ADS_STORAGE_KEY = 'seen_advertisements';

// cityId manquant, à ajouter avec les autres signaux de contexte
  cityId = this.orderContextStore.cityId;
  // Editing Flow Control Elements
  editingCartItemId: string | null = null;
  private returnToCommand = false;
  private editingData: {
    existingBuyItems?: any[];
    existingOfferedItems?: any[];
    existingCustomizedItems?: any;
    selectedRuleRef?: string | null;

  } | null = null;

  get isDelivery(): boolean {
    return this.selectedOrderType() === TypeOrderEnum.DELIVERY;
  }

  get isTakeawayOrOnSite(): boolean {
    const orderType = this.selectedOrderType();
    return orderType === TypeOrderEnum.TAKEAWAY || orderType === TypeOrderEnum.ON_SITE;
  }

  constructor() {
  }

  ngOnInit() {
    this.isMilano = this.tenantService.getActiveTenant() === 'milano';
    this.hideFloatingCartService.setHide(true);
    this.promoName = this.route.snapshot.paramMap.get('name') || '';

    this.loadPromoWithState();

    // Standard stream tracking clean of browser-only logic blocks
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.loadPromoWithState();
    });
  }

  readonly loyaltyBalance = signal<number | null>(null);
  selectedRuleId: string | null = null;

  get availablePoints(): number {
    const balance = this.loyaltyBalance();
    return balance === null ? 0 : Math.max(balance - this.getPointsAlreadyInCart(), 0);
  }

  getSelectedRule(): any | null {
    return this.promoSignal()?.payload?.rules?.find((r: any) => r.id === this.selectedRuleId) ?? null;
  }

  canAffordRule(rule: any): boolean {
    return this.loyaltyBalance() !== null && (rule.loyaltyPoints ?? 0) <= this.availablePoints;
  }

  getRuleDiscountLabel(rule: any): string {
    const d = rule.discount ?? 0;
    return d >= 1 ? 'Offert' : d > 0 ? `-${Math.round(d * 100)}%` : '';
  }

  private loadLoyaltyBalance(): void {
    if (!this.isBrowser) return;
    this.customerService.getProfile().pipe(
      map((res: any) => Number(res.body?.data?.loyaltyPoints ?? 0)),
      catchError(() => of(null)),
      takeUntil(this.destroy$)
    ).subscribe(balance => this.loyaltyBalance.set(balance));
  }

  selectRule(rule: any): boolean {
    if (this.selectedRuleId === rule.id) return true;

    if (this.loyaltyBalance() === null) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Connexion requise',
        'Veuillez vous connecter pour utiliser vos points de fidélité.');
      return false;
    }
    if (!this.canAffordRule(rule)) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Solde de points insuffisant',
        `Cette option nécessite ${rule.loyaltyPoints} pts, mais vous n'avez que ${this.availablePoints} pts disponibles.`);
      return false;
    }

    // une seule règle: on vide les autres
    (this.promoSignal()?.payload?.rules ?? []).forEach((r: any) => {
      if (r.id !== rule.id) this.resetRule(r);
    });
    this.selectedRuleId = rule.id;
    return true;
  }

  private resetRule(rule: any): void {
    rule.products.forEach((p: any) => {
      if (this.selectedItemQuantities[rule.id]) this.selectedItemQuantities[rule.id][p.id] = 0;
      delete this.customizedItems[this.getItemKey(rule.id, p.id)];
    });
  }

  private addPointsPromoToCart(): void {
    const rule = this.getSelectedRule();
    if (!rule) return;

    this.isSubmitting.set(true);
    this.verifyPointsThen(rule.loyaltyPoints, () => {
      const previousCartItemsSnapshot = JSON.parse(JSON.stringify(this.cartService.items()));

      if (this.editingCartItemId) {
        this.cartService.removeItem(this.editingCartItemId);
      }

      const promo = this.promoSignal();
      const customized: { [key: string]: any } = {};

      const buyItems = this.getSelectedBuyItems().map(item => {
        let base = 0, supp = 0;
        const baseKey = this.getItemKey(item.rule.id, item.product.id);
        for (let i = 0; i < item.quantity; i++) {
          base += this.getProductBasePrice(item.product, item.rule);
          supp += this.getCustomizationExtraPrice(item.rule.id, item.product.id, i);
          if (this.customizedItems[baseKey]?.[i]) customized[`${baseKey}_${i}`] = this.customizedItems[baseKey][i];
        }
        return {
          product: item.product, quantity: item.quantity, rule: item.rule,
          unitBasePrice: base / item.quantity, unitSupplementsPrice: supp / item.quantity,
          unitPrice: (base + supp) / item.quantity,
          totalBasePrice: base, totalSupplementsPrice: supp, totalPrice: base + supp
        };
      });

      const total = this.calculatePromotionTotal();

      this.cartService.addPromotion({
        promotion: {
          ...promo,
          payload: {
            ...promo.payload,
            invertedDueToCheapestOffered: false,
            promotionBaseTotal: this.calculatePromotionBaseTotal(),
            totalSupplementsPrice: this.getTotalSupplementsPrice(),
            selectedRuleRef: rule.id
          }
        },
        promotionImage: promo.image || '',
        buyItems,
        offeredItems: [],
        customizedItems: customized,
        total,
        redeemWithPoints: true,
        pointsCost: rule.loyaltyPoints
      });

      const apiOrderType = this.cartService.getOrderTypeForAPI();
      this.cartService.saveCartToServer(apiOrderType).subscribe({
        next: () => {
          this.editingCartItemId = null;
          if (this.returnToCommand) {
            this.router.navigate(['/command'], {queryParams: {mode: 'redeem'}});
          } else {
            this.notificationService.sendNotification(
              NotificationType.SUCCESS,
              `Offre ajoutée au panier (${rule.loyaltyPoints} pts)`,
              ''
            );
            this.router.navigate(['/product'], {queryParams: {mode: 'redeem'}});
          }
        },
        error: (err) => {
          this.isSubmitting.set(false);
          this.cartService.restoreItems(previousCartItemsSnapshot);
          this.handleCartSaveError(err);
        }
      });
    });
  }
  private loadPromoWithState() {
    let state: any = null;
    const navigation = this.router.currentNavigation();
    if (navigation?.extras?.state) {
      state = navigation.extras.state;
    }
    if (!state && this.isBrowser) {
      state = (window as any).history.state;
    }

    this.returnToCommand = state?.returnToCommand === true;
    const newPromoId = state?.promoSlug || this.route.snapshot.paramMap.get('name');
    const newEditingCartItemId = state?.editingCartItemId || null;
    this.fromMenuFlow = state?.fromMenu === true || !!state?.promoId;

    // Si on est déjà sur la même promo mais que le type/ville de commande a changé
    if (newPromoId === this.promoId && newEditingCartItemId === this.editingCartItemId) {
      this.checkAndShowAdvertisement(); // 👈 Forcer l'appel de pub avec la nouvelle ville/type
      return;
    }

    this.resetState();
    this.promoId = newPromoId;
    this.editingCartItemId = newEditingCartItemId;

    if (this.editingCartItemId && state) {
      this.editingData = {
        existingBuyItems: state.existingBuyItems,
        existingOfferedItems: state.existingOfferedItems,
        existingCustomizedItems: state.existingCustomizedItems,
        selectedRuleRef: state.selectedRuleRef ?? null,

      };
    } else {
      this.editingData = null;
    }

    if (this.promoId) {
      this.fetchPromotionData(this.promoId);
    } else {
      console.warn('Aucun promoId trouvé');
      this.router.navigate(['/product']);
    }
  }

  private resetState() {
    this.selectedItemQuantities = {};
    this.customizedItems = {};
    this.customizableItems = {};
    this.productRequiredAdditions = {};
    this.fullProductsCache = {};
    this.isLoading.set(true);
    this.editingCartItemId = null;
    this.editingData = null;
    this.selectedRuleId = null;

  }

  private fetchPromotionData(slug: string) {
    this.isLoading.set(true);

    this.promosService.getPromotionBySlug(slug).pipe(
      catchError((err) => {
        console.error("Erreur de récupération de la promotion", err);
        this.notificationService.sendNotification(NotificationType.ERROR, 'Impossible de charger la promotion', '');
        this.isLoading.set(false);
        this.router.navigate(['/product']);
        return of(null);
      }),
      takeUntil(this.destroy$)
    ).subscribe((result) => {
      if (!result) return;

      const promoData = this.normalizePromo(result.body?.data || result);
      this.promoSignal.set(promoData);
      this.initializePromoData(promoData);
      if (this.isPointsPromo) this.loadLoyaltyBalance();

      this.setSeoTags();

      if (this.fromMenuFlow) {
        this.checkAndShowAdvertisement(); // 👈 ajouté
      }
    });
  }

  get isPointsPromo(): boolean {
    return this.promoSignal()?.type === 'loyalty-point-redemption';
  }

  private getRule(ruleId: string) {
    return this.promoSignal()?.payload?.rules?.find((r: any) => r.id === ruleId);
  }

  getRuleDiscount(ruleId: string): number {
    return this.getRule(ruleId)?.discount ?? 0;
  }

  getPointsCost(): number {
    return this.getSelectedRule()?.loyaltyPoints ?? 0;
  }

// Reste à payer en euros après remise par sélection (hors suppléments)
  private getPointsEuroBase(): number {
    return this.getSelectedBuyItems().reduce((sum, item) => {
      const discountedUnitPrice = Math.round((this.getProductBasePrice(item.product, item.rule) * (1 - (item.rule.discount ?? 0)) + Number.EPSILON) * 100) / 100;
      return sum + (discountedUnitPrice * item.quantity);
    }, 0);
  }

  private getSeenAdvertisementIds(): string[] {
    if (!this.isBrowser) return [];
    try {
      const raw = localStorage.getItem(this.SEEN_ADS_STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private normalizePromo(promo: any): any {
    if (promo?.type !== 'loyalty-point-redemption' || !promo.payload?.selections) return promo;

    const rules: any[] = [];

    promo.payload.selections.forEach((sel: any) => {
      const rawCategories = Array.isArray(sel.categories) ? sel.categories : [sel];

      const combinedCategoriesName = rawCategories.map((cat: any) => {
        const qty = cat.quantity || sel.quantity || 1;
        return `${qty} ${cat.name || cat.category?.name || 'Catégorie'}`;
      }).join(' + ');

      // Extraction et structuration par catégorie
      const categoriesDetails = rawCategories.map((cat: any) => {
        // Normalisation du champ size
        let sizes: any[] = [];
        if (cat.size) {
          sizes = Array.isArray(cat.size)
            ? cat.size
            : [typeof cat.size === 'object' ? cat.size : { name: cat.size }];
        }

        return {
          id: cat.id || cat.category?.id || `cat_${Math.random()}`,
          name: cat.name || cat.category?.name || 'Catégorie',
          quantity: cat.quantity || 1,
          sizes,
          products: (cat.products || []).map((p: any) => (typeof p === 'string' ? { id: p, name: p } : p))
        };
      });

      const allProducts = categoriesDetails.flatMap((cat: any) => cat.products);

      // Récupérer toutes les tailles distinctes de cette sélection
      const allSizesMap = new Map<string, any>();
      categoriesDetails.forEach((cat: any) => {
        cat.sizes?.forEach((s: any) => {
          if (s?.name && !allSizesMap.has(s.name)) {
            allSizesMap.set(s.name, s);
          }
        });
      });
      const allSizes = Array.from(allSizesMap.values());

      rules.push({
        id: sel.ref || `rule_${Math.random()}`,
        quantity: rawCategories.reduce((sum: number, cat: any) => sum + (cat.quantity || 1), 0),
        sortOrder: sel.order || 1,
        offered: false,
        category: {
          id: sel.ref,
          name: combinedCategoriesName
        },
        categoriesDetails,
        sizes: allSizes, // <-- Remplacer [] par allSizes pour que rule.sizes contienne la taille
        products: allProducts,
        loyaltyPoints: sel.loyaltyPoints,
        discount: sel.discount
      });
    });

    return {
      ...promo,
      payload: {
        ...promo.payload,
        rules,
        cheapestOffered: false,
        reduction: null
      }
    };
  }
  private markAdvertisementAsSeen(adId: string): void {
    if (!this.isBrowser) return;
    const seenIds = this.getSeenAdvertisementIds();
    if (!seenIds.includes(adId)) {
      seenIds.push(adId);
      localStorage.setItem(this.SEEN_ADS_STORAGE_KEY, JSON.stringify(seenIds));
    }
  }

  private checkAndShowAdvertisement(): void {
    if (!this.isBrowser) return;
    this.adFlowService.checkAndShowAdvertisements(this.cityId(), this.isDelivery);
  }

  private initializePromoData(promo: any) {
    if (promo?.payload?.rules) {
      promo.payload.rules.sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0));

      promo.payload.rules.forEach((rule: any) => {
        this.selectedItemQuantities[rule.id] = {};
        rule.products.forEach((product: any) => {
          this.selectedItemQuantities[rule.id][product.id] = 0;
        });
      });
    }

    this.isLoading.set(false);
    if (this.editingCartItemId && this.editingData) {
      this.prefillPromoEditingData();
    }
  }

  private prefillPromoEditingData(): void {
    if (!this.editingData) return;

    if (this.isPointsPromo) {
      this.selectedRuleId =
        this.editingData.selectedRuleRef
        ?? this.editingData.existingBuyItems?.[0]?.rule?.id
        ?? null;
    }

    const allItems = [
      ...(this.editingData.existingBuyItems || []),
      ...(this.editingData.existingOfferedItems || [])
    ];

    allItems.forEach(item => {
      if (!this.selectedItemQuantities[item.rule.id]) {
        this.selectedItemQuantities[item.rule.id] = {};
      }

      const currentQty = this.selectedItemQuantities[item.rule.id][item.product.id] || 0;
      this.selectedItemQuantities[item.rule.id][item.product.id] = currentQty + item.quantity;

      const itemKey = this.getItemKey(item.rule.id, item.product.id);
      this.customizableItems[itemKey] = true;

      if (item.product?.id && (!item.product.price || typeof item.product.price !== 'number')) {
        this.productService.getProductById(item.product.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: any) => {
              const fullProduct = res.body?.data;
              if (fullProduct) {
                this.fullProductsCache[item.product.id] = fullProduct;
                this.promoSignal.set({...this.promoSignal()});
              }
            }
          });
      } else if (item.product?.id) {
        this.fullProductsCache[item.product.id] = item.product;
      }
    });

    if (this.editingData.existingCustomizedItems) {
      this.customizedItems = {};
      Object.keys(this.editingData.existingCustomizedItems).forEach(key => {
        const parts = key.split('_');
        if (parts.length >= 3) {
          const ruleId = parts[0];
          const productId = parts[1];
          const index = parseInt(parts[2]);
          const baseKey = `${ruleId}_${productId}`;

          if (!this.customizedItems[baseKey]) {
            this.customizedItems[baseKey] = {};
          }
          // Copier proprement l'élément personnalisé sans conserver de références corrompues
          this.customizedItems[baseKey][index] = JSON.parse(JSON.stringify(this.editingData!.existingCustomizedItems[key]));
          const product = this.editingData!.existingCustomizedItems[key]?.product;
          if (product) this.fullProductsCache[product.id] = product;
        }
      });
    }
  }
  getQuantityForCategory(ruleId: string, categoryGroup: any): number {
    return (categoryGroup.products || []).reduce(
      (sum: number, p: any) => sum + this.getItemQuantity(ruleId, p.id), 0);
  }
  incrementItem(ruleId: string, productId: string, ruleIndex?: number) {
    if (this.isPointsPromo) {
      const r = this.getRule(ruleId);
      if (!r || !this.selectRule(r)) return;
    }

    if (!this.selectedItemQuantities[ruleId]) {
      this.selectedItemQuantities[ruleId] = {};
    }

    if (!this.selectedItemQuantities[ruleId][productId]) {
      this.selectedItemQuantities[ruleId][productId] = 0;
    }

    const promo = this.promoSignal();
    const rule = promo?.payload?.rules.find((r: any) => r.id === ruleId);

    if (rule) {
      const totalQty = this.getTotalQuantityForRule(ruleId);

      if (totalQty >= rule.quantity) {
        const categoryName = rule.category?.name || 'cette catégorie';
        this.notificationService.sendNotification(
          NotificationType.WARNING,
          `Vous avez déjà sélectionné votre "${categoryName}" (${rule.quantity} max)`,
          ''
        );
        return;
      }
    }
    if (this.isPointsPromo && rule?.categoriesDetails) {
      const cat = rule.categoriesDetails.find((c: any) =>
        c.products.some((p: any) => p.id === productId));
      if (cat && this.getQuantityForCategory(ruleId, cat) >= cat.quantity) {
        this.notificationService.sendNotification(
          NotificationType.WARNING,
          `Vous avez déjà sélectionné votre "${cat.name}" (${cat.quantity} max)`,
          ''
        );
        return;
      }
    }

    this.selectedItemQuantities[ruleId][productId]++;

    this.checkIfCustomizableAndHandleIncrement(ruleId, productId, ruleIndex);

    if (this.isBrowser && window.innerWidth < 1024 && this.canAddToCart() && !this.isCartSheetOpen) {
      setTimeout(() => this.toggleCartSheet(), 400);
    }
  }

  private checkIfCustomizableAndHandleIncrement(ruleId: string, itemId: string, ruleIndex?: number) {
    const itemKey = this.getItemKey(ruleId, itemId);
    const promo = this.promoSignal();
    const rule = promo?.payload?.rules.find((r: any) => r.id === ruleId);

    if (!rule) return;

    const newTotalQty = this.getTotalQuantityForRule(ruleId);
    const isRuleComplete = newTotalQty === rule.quantity;

    let selectedSize: string | null = null;
    if (rule?.sizes && rule.sizes.length > 0) {
      selectedSize = rule.sizes[0].name;
    }

    if (this.customizableItems[itemKey] !== undefined) {
      const hasRequired = this.productRequiredAdditions[itemKey];

      if (hasRequired) {
        const currentItemQty = this.selectedItemQuantities[ruleId]?.[itemId] || 0;
        const newItemIndex = currentItemQty - 1;
        const cachedProduct = this.fullProductsCache[itemId];
        if (cachedProduct) {
          setTimeout(() => {
            this.openProductCustomizationWithRuleCompletion(
              cachedProduct,
              ruleId,
              newItemIndex,
              isRuleComplete,
              ruleIndex
            );
          }, 100);
        } else {
          this.productService.getProductById(itemId).pipe(takeUntil(this.destroy$)).subscribe({
            next: (res: any) => {
              const product = res.body?.data;
              this.fullProductsCache[itemId] = product;
              setTimeout(() => {
                this.openProductCustomizationWithRuleCompletion(
                  product,
                  ruleId,
                  newItemIndex,
                  isRuleComplete,
                  ruleIndex
                );
              }, 100);
            }
          });
        }
      } else {
        if (isRuleComplete) {
          this.checkAndScrollIfRuleComplete(ruleId, ruleIndex);
        }
      }
      return;
    }
    if (this.fullProductsCache[itemId]) {
      const product = this.fullProductsCache[itemId];
      const filteredAdditions = this.getFilteredAdditionsForProduct(product?.additions || [], selectedSize);
      const hasRequiredAdditions = filteredAdditions.some((add: any) => add.required);

      const isCustomizable = product?.additions?.length > 0 ||
        product?.ingredients?.length > 0 ||
        product?.supplements?.length > 0;

      this.customizableItems[itemKey] = isCustomizable;
      this.productRequiredAdditions[itemKey] = hasRequiredAdditions;

      if (hasRequiredAdditions) {
        const currentItemQty = this.selectedItemQuantities[ruleId]?.[itemId] || 0;
        for (let i = 0; i < currentItemQty; i++) {
          const isLastItem = (i === currentItemQty - 1);
          setTimeout(() => {
            this.openProductCustomizationWithRuleCompletion(
              product, ruleId, i,
              isRuleComplete && isLastItem,
              ruleIndex
            );
          }, i * 100);
        }
      } else {
        if (isRuleComplete) this.checkAndScrollIfRuleComplete(ruleId, ruleIndex);
      }
      return;
    }

    this.productService.getProductById(itemId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        const product = res.body?.data;
        this.fullProductsCache[itemId] = product;

        const isCustomizable = product?.additions?.length > 0 ||
          product?.ingredients?.length > 0 ||
          product?.supplements?.length > 0;

        const filteredAdditions = this.getFilteredAdditionsForProduct(product?.additions || [], selectedSize);
        const hasRequiredAdditions = filteredAdditions.some((add: any) => add.required);

        this.customizableItems[itemKey] = isCustomizable;
        this.productRequiredAdditions[itemKey] = hasRequiredAdditions;

        if (hasRequiredAdditions) {
          const currentItemQty = this.selectedItemQuantities[ruleId]?.[itemId] || 0;
          for (let i = 0; i < currentItemQty; i++) {
            const isLastItem = (i === currentItemQty - 1);
            setTimeout(() => {
              this.openProductCustomizationWithRuleCompletion(
                product, ruleId, i,
                isRuleComplete && isLastItem,
                ruleIndex
              );
            }, i * 100);
          }
        } else {
          if (isRuleComplete) this.checkAndScrollIfRuleComplete(ruleId, ruleIndex);
        }
      },
      error: () => {
        this.customizableItems[itemKey] = false;
        this.productRequiredAdditions[itemKey] = false;
        if (isRuleComplete) this.checkAndScrollIfRuleComplete(ruleId, ruleIndex);
      }
    });
  }

  private openProductCustomizationWithRuleCompletion(
    product: any,
    ruleId: string,
    index: number,
    isRuleComplete: boolean,
    ruleIndex?: number
  ) {
    const itemKey = this.getItemKey(ruleId, product.id);
    const existingCustomization = this.customizedItems[itemKey]?.[index];

    const promo = this.promoSignal();
    const rule = promo?.payload?.rules.find((r: any) => r.id === ruleId);
    let selectedSize: string | null = null;
    if (rule?.sizes && rule.sizes.length > 0) {
      selectedSize = rule.sizes[0].name;
    }

    const dialogRef = this.dialog.open(DetailsProductMenusComponent, {
      width: '95%',
      maxWidth: '600px',
      panelClass: 'custom-dialog',
      data: {
        productId: product.id,
        product: product,
        existingCustomization,
        preselectedSize: selectedSize
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result) {
        if (!this.customizedItems[itemKey]) {
          this.customizedItems[itemKey] = {};
        }
        this.customizedItems[itemKey][index] = result;
      }

      if (isRuleComplete && ruleIndex !== undefined) {
        const promo = this.promoSignal();
        if (!promo?.payload?.rules) return;

        const rule = promo.payload.rules[ruleIndex];
        if (rule) {
          this.handleRuleCompletionScroll(ruleIndex, rule);
        }
      }
    });
  }

  private handleRuleCompletionScroll(ruleIndex: number, rule: any) {
    if (!this.isBrowser) return; // Guard viewport animations against server crashes

    const promo = this.promoSignal();
    if (!promo?.payload?.rules) return;

    if (!rule.offered) {
      if (this.areAllBuyRulesComplete()) {
        setTimeout(() => this.scrollToOfferedSection(), 300);
      } else {
        setTimeout(() => this.scrollToNextBuyRule(ruleIndex), 300);
      }
    } else {
      setTimeout(() => this.scrollToNextOfferedRule(ruleIndex), 300);
    }
  }

  private checkAndScrollIfRuleComplete(ruleId: string, ruleIndex?: number) {
    const promo = this.promoSignal();
    if (!promo?.payload?.rules) return;

    const rule = promo.payload.rules.find((r: any) => r.id === ruleId);
    if (!rule || ruleIndex === undefined) return;

    const totalQty = this.getTotalQuantityForRule(ruleId);
    if (totalQty === rule.quantity) {
      this.handleRuleCompletionScroll(ruleIndex, rule);
    }
  }

  private scrollToNextBuyRule(currentRuleIndex: number) {
    if (!this.isBrowser) return;
    const promo = this.promoSignal();
    if (!promo?.payload?.rules) return;

    const width = window.innerWidth;
    const prefix = width < 768 ? 'mobile-rule-' : width < 1024 ? 'tablet-rule-' : 'desktop-rule-';

    const buyRules = promo.payload.rules
      .map((rule: any, index: number) => ({rule, index}))
      .filter((item: any) => !item.rule.offered && this.hasProductsToBuy());

    const currentBuyRulePosition = buyRules.findIndex((item: any) => item.index === currentRuleIndex);
    if (currentBuyRulePosition !== -1 && currentBuyRulePosition < buyRules.length - 1) {
      const nextBuyRule = buyRules[currentBuyRulePosition + 1];
      const nextElement = document.getElementById(`${prefix}${nextBuyRule.index}`);
      if (nextElement) this.performScroll(nextElement, width);
    }
  }

  private scrollToNextOfferedRule(currentRuleIndex: number) {
    if (!this.isBrowser) return;
    const promo = this.promoSignal();
    if (!promo?.payload?.rules) return;

    const width = window.innerWidth;
    const prefix = width < 768 ? 'mobile-rule-' : width < 1024 ? 'tablet-rule-' : 'desktop-rule-';

    const offeredRules = promo.payload.rules
      .map((rule: any, index: number) => ({rule, index}))
      .filter((item: any) => item.rule.offered && this.hasOfferedProducts());

    const currentOfferedRulePosition = offeredRules.findIndex((item: any) => item.index === currentRuleIndex);
    if (currentOfferedRulePosition !== -1 && currentOfferedRulePosition < offeredRules.length - 1) {
      const nextOfferedRule = offeredRules[currentOfferedRulePosition + 1];
      const nextElement = document.getElementById(`${prefix}${nextOfferedRule.index}`);
      if (nextElement) this.performScroll(nextElement, width);
    }
  }

  scrollToOfferedSection() {
    if (!this.isBrowser) return;
    const width = window.innerWidth;
    const prefix = width < 768 ? 'mobile-rule-' : width < 1024 ? 'tablet-rule-' : 'desktop-rule-';

    const promo = this.promoSignal();
    if (!promo?.payload?.rules) return;

    const firstOfferedRuleIndex = promo.payload.rules.findIndex((r: any) => r.offered);
    if (firstOfferedRuleIndex !== -1) {
      const firstOfferedElement = document.getElementById(`${prefix}${firstOfferedRuleIndex}`);
      if (firstOfferedElement) this.performScroll(firstOfferedElement, width);
    }
  }

  private performScroll(element: HTMLElement, width: number) {
    const offset = width < 768 ? 80 : width < 1024 ? 100 : 120;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }

  toggleCartSheet() {
    this.isCartSheetOpen = !this.isCartSheetOpen;
    if (this.isBrowser) {
      document.body.style.overflow = this.isCartSheetOpen ? 'hidden' : '';
    }
    if (this.isCartSheetOpen && this.scrollableContent) {
      setTimeout(() => {
        if (this.scrollableContent) {
          this.scrollableContent.nativeElement.scrollTop = 0;
        }
      });
    }
  }

  closeCartSheet() {
    this.isCartSheetOpen = false;
    this.sheetTransform = 'translateY(0px)';
    if (this.isBrowser) {
      document.body.style.overflow = '';
    }
    if (this.scrollableContent) {
      this.scrollableContent.nativeElement.scrollTop = 0;
    }
  }

  // --- Browser Specific UI Touch Trackers ---
  onTouchStart(event: TouchEvent) {
    this.touchStartY = event.touches[0].clientY;
  }

  onTouchMove(event: TouchEvent) {
    this.touchCurrentY = event.touches[0].clientY;
    const deltaY = this.touchCurrentY - this.touchStartY;
    if (deltaY > 0) {
      this.sheetTransform = `translateY(${deltaY}px)`;
    }
  }

  onTouchEnd() {
    const deltaY = this.touchCurrentY - this.touchStartY;
    if (deltaY > this.SWIPE_CLOSE_THRESHOLD) {
      this.closeCartSheet();
    } else {
      this.sheetTransform = 'translateY(0px)';
    }
  }

  // --- Core Calculation & Static Logic Domain Rules (Unchanged functionality) ---
  decrementItem(ruleId: string, productId: string) {
    if (!this.selectedItemQuantities[ruleId]?.[productId]) return;
    this.selectedItemQuantities[ruleId][productId]--;

    if (this.selectedItemQuantities[ruleId][productId] <= 0) {
      this.selectedItemQuantities[ruleId][productId] = 0;
      const itemKey = this.getItemKey(ruleId, productId);
      if (this.customizedItems[itemKey]) delete this.customizedItems[itemKey];
    }
  }

  openDialog(categoryId?: string) {
    const promo = this.promoSignal();
    const orderTypes = promo?.orderTypes || [];
    const typedOrderTypes = this.convertToTypeOrderEnum(orderTypes);

    const dialogRef = this.dialog.open(OrderComponent, {
      panelClass: 'custom-dialog',
      autoFocus: false,
      disableClose: true,
      data: {
        selectedCategoryId: categoryId,
        preselectedOrderType: this.selectedOrderType(),
        allowedOrderTypes: typedOrderTypes,
        context: 'promo',
        promoSlug: this.promoName,
        promoId: this.promoId, // 👈 1. Passer le promoId
        stayOnPromoPage: true
      }
    });

    // 👈 2. Appeler la pub dès que l'utilisateur valide et ferme le dialogue
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result) {
        this.checkAndShowAdvertisement();
      }
    });
  }

  getProductPrice(product: any, rule: any): number {
    if (rule?.sizes && rule.sizes.length > 0) {
      const sizeName = rule.sizes[0].name;
      if (product.price && typeof product.price === 'object' && !Array.isArray(product.price)) {
        const priceStr = product.price[sizeName];
        if (priceStr !== undefined) return this.parsePrice(priceStr);
      }
      if (this.fullProductsCache[product.id]) {
        const priceEntry = this.fullProductsCache[product.id].pricePerSizes?.find((entry: any) => entry.size.name === sizeName);
        if (priceEntry?.price !== undefined) return Number(priceEntry.price);
      }
    }
    return product.price !== undefined && product.price !== null ? (typeof product.price === 'number' ? product.price : this.parsePrice(product.price)) : 0;
  }

  private parsePrice(priceStr: string | number): number {
    if (typeof priceStr === 'number') return priceStr;
    if (!priceStr) return 0;
    const num = parseFloat(priceStr.toString().replace(/€/g, '').replace(/\s/g, '').trim().replace(',', '.'));
    return isNaN(num) ? 0 : num;
  }

  private convertToTypeOrderEnum(orderTypes: string[]): TypeOrderEnum[] {
    const typeMap: { [key: string]: TypeOrderEnum } = {
      'À Emporter': TypeOrderEnum.TAKEAWAY,
      'En Livraison': TypeOrderEnum.DELIVERY,
      'Sur Place': TypeOrderEnum.ON_SITE
    };
    return orderTypes.map(type => typeMap[type]).filter(Boolean);
  }

  private getItemKey = (ruleId: string, productId: string): string => `${ruleId}_${productId}`;
  getSizesText = (sizes: any[]): string => sizes ? sizes.map(s => s.name).join(', ') : '';
  getItemQuantity = (ruleId: string, productId: string): number => this.selectedItemQuantities[ruleId]?.[productId] || 0;
  getTotalQuantityForRule = (ruleId: string): number => this.selectedItemQuantities[ruleId] ? Object.values(this.selectedItemQuantities[ruleId]).reduce((sum: number, qty: number) => sum + qty, 0) : 0;
  private areAllBuyRulesComplete = (): boolean => !this.promoSignal()?.payload?.rules?.filter((r: any) => !r.offered).some((rule: any) => this.getTotalQuantityForRule(rule.id) !== rule.quantity);
  hasProductsToBuy = (): boolean => !!this.promoSignal()?.payload?.rules?.some((rule: any) => !rule.offered);
  hasOfferedProducts = (): boolean => !!this.promoSignal()?.payload?.rules?.some((rule: any) => rule.offered);

  getSelectedBuyItems(): Array<{ product: any, quantity: number, rule: any }> {
    const promo = this.promoSignal();
    if (!promo?.payload?.rules) return [];

    if (!promo.payload.cheapestOffered) {
      const items: any[] = [];
      promo.payload.rules.forEach((rule: any) => {
        if (!rule.offered) {
          rule.products.forEach((product: any) => {
            const qty = this.getItemQuantity(rule.id, product.id);
            if (qty > 0) items.push({product, quantity: qty, rule});
          });
        }
      });
      return items;
    }
    const allFlat = this.getAllSelectedItemsFlat();
    if (allFlat.length === 0) return [];
    const grouped = new Map<string, { product: any, quantity: number, rule: any }>();
    [...allFlat].sort((a, b) => a.price - b.price).slice(this.getTotalOfferedQuantity()).forEach(item => {
      const key = `${item.rule.id}_${item.product.id}`;
      if (grouped.has(key)) grouped.get(key)!.quantity++;
      else grouped.set(key, {product: item.product, quantity: 1, rule: item.rule});
    });
    return Array.from(grouped.values());
  }

  getOfferedItems(): Array<{ product: any, quantity: number, rule: any }> {
    const promo = this.promoSignal();
    if (!promo?.payload?.rules) return [];
    if (!promo.payload.cheapestOffered) {
      const items: any[] = [];
      promo.payload.rules.forEach((rule: any) => {
        if (rule.offered) {
          rule.products.forEach((product: any) => {
            const qty = this.getItemQuantity(rule.id, product.id);
            if (qty > 0) items.push({product, quantity: qty, rule});
          });
        }
      });
      return items;
    }
    const allFlat = this.getAllSelectedItemsFlat();
    if (allFlat.length === 0) return [];
    const grouped = new Map<string, { product: any, quantity: number, rule: any }>();
    [...allFlat].sort((a, b) => a.price - b.price).slice(0, this.getTotalOfferedQuantity()).forEach(item => {
      const key = `${item.rule.id}_${item.product.id}`;
      if (grouped.has(key)) grouped.get(key)!.quantity++;
      else grouped.set(key, {product: item.product, quantity: 1, rule: item.rule});
    });
    return Array.from(grouped.values());
  }

  openProductCustomization(product: any, ruleId: string, index: number = 0) {
    const itemKey = this.getItemKey(ruleId, product.id);
    const existingCustomization = this.customizedItems[itemKey]?.[index];
    const rule = this.promoSignal()?.payload?.rules.find((r: any) => r.id === ruleId);

    this.dialog.open(DetailsProductMenusComponent, {
      width: '95%',
      maxWidth: '600px',
      panelClass: 'custom-dialog',
      data: {
        productId: product.id,
        existingCustomization,
        product: this.fullProductsCache[product.id] || null,
        preselectedSize: rule?.sizes?.[0]?.name || null
      }
    }).afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result) {
        if (!this.customizedItems[itemKey]) this.customizedItems[itemKey] = {};
        this.customizedItems[itemKey][index] = result;
      }
    });
  }

  isItemCustomized = (ruleId: string, productId: string, index: number = 0): boolean => !!this.customizedItems[this.getItemKey(ruleId, productId)]?.[index];

  hasIncompleteRequiredCustomizations(ruleId: string, productId: string, index: number = 0): boolean {
    const itemKey = this.getItemKey(ruleId, productId);
    if (!this.productRequiredAdditions[itemKey]) return false;

    const customization = this.customizedItems[itemKey]?.[index];
    if (!customization?.product) return true;

    const rule = this.promoSignal()?.payload?.rules.find((r: any) => r.id === ruleId);
    const selectedSize = rule?.sizes?.[0]?.name || null;
    const filteredAdditions = this.getFilteredAdditionsForProduct(customization.product.additions || [], selectedSize);

    return filteredAdditions.some(addition => addition.required && this.getTotalSelectedForAddition(customization, addition.id) !== (addition.quantity || 1));
  }

  getTotalSelectedForAddition(customization: any, additionId: string): number {
    return customization.selectedAdditions?.[additionId] ? Object.values(customization.selectedAdditions[additionId] as Record<string, number>).reduce((sum: number, qty: number) => sum + qty, 0) : 0;
  }

  getCustomizationSummary(ruleId: string, productId: string, index: number = 0): string {
    const customization = this.customizedItems[this.getItemKey(ruleId, productId)]?.[index];
    if (!customization) return '';

    const rule = this.promoSignal()?.payload?.rules.find((r: any) => r.id === ruleId);
    const selectedSize = rule?.sizes?.[0]?.name || null;
    const parts: string[] = [];

    if (customization.excludedIngredients?.length > 0) {
      const excludedNames = customization.excludedIngredients.map((id: string) => customization.product.ingredients?.find((i: any) => i.id === id)?.name).filter(Boolean).join(', ');
      if (excludedNames) parts.push(`❌ Sans: ${excludedNames}`);
    }
    if (customization.selectedAdditions) {
      const additionParts: string[] = [];
      const filteredAdditions = this.getFilteredAdditionsForProduct(customization.product.additions || [], selectedSize);
      Object.keys(customization.selectedAdditions).forEach(additionId => {
        const addition = filteredAdditions.find((a: any) => a.id === additionId);
        if (addition) {
          Object.keys(customization.selectedAdditions[additionId]).forEach(itemAdditionId => {
            const qty = customization.selectedAdditions[additionId][itemAdditionId];
            if (qty > 0) {
              const addItem = addition.items.find((i: any) => i.id === itemAdditionId);
              if (addItem) additionParts.push(`${addItem.name}${qty > 1 ? ` x${qty}` : ''}`);
            }
          });
        }
      });
      if (additionParts.length > 0) parts.push(`🍽️ Choix: ${additionParts.join(', ')}`);
    }
    if (customization.selectedSupplements && Object.keys(customization.selectedSupplements).length > 0) {
      const supplementDetails: string[] = [];
      let totalSuppPrice = 0;
      Object.keys(customization.selectedSupplements).forEach(suppId => {
        const supplement = customization.product.supplements?.find((s: any) => s.id === suppId);
        if (supplement) {
          const qty = customization.selectedSupplements[suppId] || 1;
          const unitPrice = (customization.selectedSize && supplement.pricePerSizes?.length > 0) ? (supplement.pricePerSizes.find((ps: any) => ps.size.name === customization.selectedSize)?.price || supplement.price || 0) : (supplement.price || 0);
          const itemTotal = unitPrice * qty;
          totalSuppPrice += itemTotal;
          supplementDetails.push(`[CAT]${supplement.categories?.[0] || ''}-${supplement.name}${qty > 1 ? ` x${qty}` : ''}${itemTotal > 0 ? ` (+${this.truncate2(itemTotal).toFixed(2).replace('.', ',')}€)` : ''}`);
        }
      });
      if (supplementDetails.length > 0) parts.push(`➕ Suppléments${totalSuppPrice > 0 ? ` (${this.truncate2(totalSuppPrice).toFixed(2).replace('.', ',')}€)` : ''}: ${supplementDetails.join('|||')}`);
    }
    return parts.join('\n');
  }

  getCustomizationExtraPrice(ruleId: string, productId: string, index: number = 0): number {
    const customization = this.customizedItems[this.getItemKey(ruleId, productId)]?.[index];
    if (!customization) return 0;

    let extraPrice = 0;
    if (customization.selectedAdditions) {
      Object.keys(customization.selectedAdditions).forEach(additionId => {
        const addition = customization.product.additions?.find((a: any) => a.id === additionId);
        if (addition) {
          Object.keys(customization.selectedAdditions[additionId]).forEach(itemAdditionId => {
            const qty = customization.selectedAdditions[additionId][itemAdditionId];
            if (qty > 0 && addition.items.find((i: any) => i.id === itemAdditionId)?.price) {
              extraPrice += addition.items.find((i: any) => i.id === itemAdditionId).price * qty;
            }
          });
        }
      });
    }
    if (customization.selectedSupplements) {
      Object.keys(customization.selectedSupplements).forEach(suppId => {
        const supplement = customization.product.supplements?.find((s: any) => s.id === suppId);
        if (supplement) {
          const price = (customization.selectedSize && supplement.pricePerSizes?.length > 0) ? (supplement.pricePerSizes.find((ps: any) => ps.size.name === customization.selectedSize)?.price || supplement.price || 0) : (supplement.price || 0);
          extraPrice += price * (customization.selectedSupplements[suppId] || 1);
        }
      });
    }
    return extraPrice;
  }

  getItemIndividualPrice(ruleId: string, productId: string, index: number = 0): number {
    return this.getProductBasePrice(this.promoSignal()?.payload?.rules.find((r: any) => r.id === ruleId)?.products.find((p: any) => p.id === productId), this.promoSignal()?.payload?.rules.find((r: any) => r.id === ruleId)) + this.getCustomizationExtraPrice(ruleId, productId, index);
  }

  getTotalBuyItemsPrice(): number {
    let total = 0;
    this.getSelectedBuyItems().forEach(item => {
      for (let i = 0; i < item.quantity; i++) total += this.getItemIndividualPrice(item.rule.id, item.product.id, i);
    });
    return total;
  }

  getTotalOfferedPriceAfterReduction(): number {
    const promo = this.promoSignal();
    if (!promo?.payload?.reduction) return 0;

    const offeredItems = this.getOfferedItems();
    const paidItems = this.getSelectedBuyItems();

    let totalOfferedBasePrice = 0;
    offeredItems.forEach(item => totalOfferedBasePrice += this.getProductBasePrice(item.product, item.rule) * item.quantity);
    let totalPaidBasePrice = 0;
    paidItems.forEach(item => totalPaidBasePrice += this.getProductBasePrice(item.product, item.rule) * item.quantity);

    const cheapestBaseTotal = Math.min(totalOfferedBasePrice, totalPaidBasePrice);
    const reductionAmount = promo.payload.reduction.type === 'PERCENTAGE' ? cheapestBaseTotal * promo.payload.reduction.value : Math.min(promo.payload.reduction.value, cheapestBaseTotal);

    return this.truncate2(totalOfferedBasePrice - reductionAmount);
  }

  removeItem(ruleId: string, productId: string, index: number = 0) {
    const itemKey = this.getItemKey(ruleId, productId);
    if (this.customizedItems[itemKey]?.[index]) delete this.customizedItems[itemKey][index];
    if (this.selectedItemQuantities[ruleId]?.[productId] > 0) this.selectedItemQuantities[ruleId][productId]--;
  }

  canAddToCart(): boolean {
    if (this.isPointsPromo) {
      const rule = this.getSelectedRule();
      if (!rule) return false;
      if (this.getTotalQuantityForRule(rule.id) !== rule.quantity) return false;
      if (!this.canAffordRule(rule)) return false;
      return !this.getSelectedBuyItems().some(item =>
        this.hasIncompleteRequiredCustomizations(item.rule.id, item.product.id));
    }
    const promo = this.promoSignal();
    if (!promo?.payload?.rules) return false;
    if (promo.payload.rules.some((rule: any) => this.getTotalQuantityForRule(rule.id) !== rule.quantity)) return false;
    return !this.getSelectedBuyItems().some(item => this.hasIncompleteRequiredCustomizations(item.rule.id, item.product.id)) && !this.getOfferedItems().some(item => this.hasIncompleteRequiredCustomizations(item.rule.id, item.product.id));
  }

  getValidationMessage(): string {
    if (this.isPointsPromo) {
      const rule = this.getSelectedRule();
      if (!rule) return 'Choisissez une offre';
      if (!this.canAffordRule(rule)) return `Solde insuffisant (${rule.loyaltyPoints} pts requis)`;
      const left = rule.quantity - this.getTotalQuantityForRule(rule.id);
      return left > 0 ? `Choisissez "${rule.category?.name}" (${left} choix restant${left > 1 ? 's' : ''})` : '';
    }
    const promo = this.promoSignal();
    if (!promo?.payload?.rules) return '';

    const buyRules = promo.payload.rules.filter((r: any) => !r.offered);
    const offerRules = promo.payload.rules.filter((r: any) => r.offered);

    for (const rule of buyRules) {
      const totalSelected = this.getTotalQuantityForRule(rule.id);
      if (totalSelected !== rule.quantity) {
        return `Choisissez "${rule.category?.name || 'Produit'}" (${rule.quantity - totalSelected} choix restant${(rule.quantity - totalSelected) > 1 ? 's' : ''})`;
      }
    }
    if (offerRules.length === 0) return '';

    const totalBought = buyRules.reduce((sum: any, rule: any) => sum + this.getTotalQuantityForRule(rule.id), 0);
    const requiredQty = buyRules.reduce((sum: any, rule: any) => sum + rule.quantity, 0);
    const timesOfferApplies = requiredQty > 0 ? Math.floor(totalBought / requiredQty) : 0;

    if (timesOfferApplies === 0) {
      const firstBuyRule = buyRules[0];
      return firstBuyRule ? `Ajoutez ${firstBuyRule.quantity - this.getTotalQuantityForRule(firstBuyRule.id)} "${firstBuyRule.category?.name || 'Produit'}" pour bénéficier des produits offerts` : '';
    }

    for (const rule of offerRules) {
      const totalSelected = this.getTotalQuantityForRule(rule.id);
      const maxAllowed = timesOfferApplies * rule.quantity;
      if (totalSelected !== maxAllowed) {
        const remaining = maxAllowed - totalSelected;
        return totalSelected === 0 ? `Vous pouvez choisir ${maxAllowed} "${rule.category?.name || 'Produit offert'}" offert${maxAllowed > 1 ? 's' : ''}` : `Il vous reste ${remaining} "${rule.category?.name || 'Produit offert'}" offert${remaining > 1 ? 's' : ''} à sélectionner`;
      }
    }
    return '';
  }

  private getOrderTypeForAPI(): string {

    const orderType = this.orderContextStore.orderType();
    switch (orderType) {
      case TypeOrderEnum.DELIVERY:
        return 'DELIVERY';
      case TypeOrderEnum.TAKEAWAY:
        return 'TAKEAWAY';
      case TypeOrderEnum.ON_SITE:
        return 'ON_SITE';
      default:
        return 'TAKEAWAY';
    }
  }

  getOrderTypeLabelFromKey(key: string): string {
    const typeKey = key as keyof typeof TypeOrderEnum;
    return TypeOrderEnum[typeKey] || key;
  }

  private findAnyProductWithId(productId: string): any {
    for (const it of this.cartService.items()) {
      if (it.type === CartItemType.PRODUCT) {
        const p = (it as ProductCartItem).product;
        if (p?.id === productId) return p;
      }
      if (it.type === CartItemType.BUNDLE) {
        const b = it as BundleCartItem;
        for (const cust of Object.values(b.customizedItems || {})) {
          const c = cust as any;
          if (c?.product?.id === productId) return c.product;
        }
      }
      if (it.type === CartItemType.PROMOTION) {
        const pr = it as PromotionCartItem;
        const found = [...(pr.buyItems || []), ...(pr.offeredItems || [])].find(e => e.product?.id === productId);
        if (found) return found.product;
        for (const cust of Object.values(pr.customizedItems || {})) {
          const c = cust as any;
          if (c?.product?.id === productId) return c.product;
        }
      }
    }
    return null;
  }

  private findEntityNameInCart(entityType: string, entityId: string): string {
    if (!entityId) return '';

    const promo = this.promoSignal();

    // 1. Chercher dans les produits et personnalisations de la promotion en cours
    if (promo) {
      if (entityType === 'promotion' && (promo.id === entityId || promo.slug === entityId)) {
        return promo.title;
      }

      // Parcourir toutes les règles et produits de la promo
      const rules = promo.payload?.rules || [];
      for (const rule of rules) {
        for (const product of rule.products || []) {
          const cachedProduct = this.fullProductsCache[product.id] || product;

          // A. Si c'est le produit lui-même
          if (entityType === 'product' && (product.id === entityId || cachedProduct.id === entityId)) {
            return cachedProduct.name || product.name || 'Produit';
          }

          // B. Chercher dans les Additions / Accompagnements du produit
          if (cachedProduct.additions && Array.isArray(cachedProduct.additions)) {
            for (const addition of cachedProduct.additions) {
              if (addition.items && Array.isArray(addition.items)) {
                const foundItem = addition.items.find((i: any) => i.id === entityId);
                if (foundItem) {
                  return foundItem.name;
                }
              }
            }
          }

          // C. Chercher dans les Suppléments
          if (cachedProduct.supplements && Array.isArray(cachedProduct.supplements)) {
            const foundSupp = cachedProduct.supplements.find((s: any) => s.id === entityId);
            if (foundSupp) {
              return foundSupp.name;
            }
          }

          // D. Chercher dans les Ingrédients de base
          if (cachedProduct.ingredients && Array.isArray(cachedProduct.ingredients)) {
            const foundIng = cachedProduct.ingredients.find((ing: any) => ing.id === entityId);
            if (foundIng) {
              return foundIng.name;
            }
          }
        }
      }

      // 2. Parcourir les personnalisations actuellement sélectionnées dans customizedItems
      for (const groupKey of Object.keys(this.customizedItems || {})) {
        const group = this.customizedItems[groupKey];
        if (!group) continue;

        for (const indexKey of Object.keys(group)) {
          const cust = group[+indexKey];
          if (!cust) continue;

          const custProduct = cust.product || {};

          // Additions sélectionnées
          if (cust.selectedAdditions) {
            for (const addId of Object.keys(cust.selectedAdditions)) {
              const itemsMap = cust.selectedAdditions[addId];
              if (itemsMap && itemsMap[entityId] > 0) {
                const additionDef = custProduct.additions?.find((a: any) => a.id === addId);
                const itemDef = additionDef?.items?.find((i: any) => i.id === entityId);
                return itemDef?.name || additionDef?.name || 'Ingrédient';
              }
            }
          }

          // Suppléments sélectionnés
          if (cust.selectedSupplements && cust.selectedSupplements[entityId] > 0) {
            const suppDef = custProduct.supplements?.find((s: any) => s.id === entityId);
            if (suppDef) {
              return suppDef.name;
            }
          }

          // Ingrédients exclus
          if (cust.excludedIngredients && Array.isArray(cust.excludedIngredients)) {
            if (cust.excludedIngredients.includes(entityId)) {
              const ingDef = custProduct.ingredients?.find((i: any) => i.id === entityId);
              if (ingDef) {
                return ingDef.name;
              }
            }
          }
        }
      }
    }

    // 3. Fallback : reste du panier local
    for (const item of this.cartService.items()) {
      if (item.type === CartItemType.PROMOTION) {
        const promoItem = item as PromotionCartItem;
        if (promoItem.promotion?.id === entityId) return promoItem.promotion.title;
      }
      if (item.type === CartItemType.PRODUCT) {
        const productItem = item as ProductCartItem;
        if (productItem.product?.id === entityId) return productItem.product.name;
      }
    }

    return '';
  }
  private handleCartSaveError(err: any): void {
    const errorDetails = err.error?.details;
    const errorMsg = errorDetails?.message || err.error?.message || '';
    const errorValue = errorDetails?.value;

    if (
      (errorMsg.includes('type de commande') && errorMsg.includes('indisponible')) ||
      (err.error?.code === 'illegal_value' && ['DELIVERY', 'TAKEAWAY', 'ON_SITE'].includes(errorValue))
    ) {
      const rawType = errorValue || this.getOrderTypeForAPI();
      const translatedType = this.getOrderTypeLabelFromKey(rawType);
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Le mode "${translatedType}" n'est pas disponible actuellement.`,
        'Veuillez modifier votre type de commande.'
      );
      return;
    }

    let detailMsg: string = err.error?.details?.message || err.error?.message || '';
    Object.keys(TypeOrderEnum).forEach((key) => {
      if (detailMsg.includes(key)) {
        const translatedLabel = this.getOrderTypeLabelFromKey(key);
        detailMsg = detailMsg.replace(new RegExp(key, 'g'), translatedLabel);
      }
    });

    if (detailMsg.includes('CUSTOMER_NOT_ALLOWED')) {
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Ce code promo appartient à un autre utilisateur, vous n’avez pas le droit de l’utiliser.`,
        ''
      );
      return;
    }

    if (detailMsg.includes('NOT_STARTED_YET')) {
      const parts = detailMsg.split('/');
      const promoCode = parts[1] || '';
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Le code promo "${promoCode}" n'est pas encore disponible.`,
        ''
      );
      return;
    }

    if (detailMsg.includes('EXPIRED')) {
      const parts = detailMsg.split('/');
      const promoCode = parts[1] || '';
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Le code promo "${promoCode}" a expiré.`,
        ''
      );
      return;
    }

    if (detailMsg === 'ORDER_RATE_LIMIT_EXCEEDED') {
      const details = err.error?.details?.details;
      let timeMessage = 'quelques minutes';
      if (details?.allowedAfter) {
        const matchMinutes = details.allowedAfter.match(/PT(?:(\d+)M)?/);
        if (matchMinutes && matchMinutes[1]) {
          timeMessage = `${matchMinutes[1]} minute(s)`;
        }
      }
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Limite de commandes atteinte : Vous avez déjà passé ${details?.orders || 'plusieurs'} commande(s).`,
        `Par sécurité, merci de patienter encore ${timeMessage} avant de pouvoir commander à nouveau.`
      );
      return;
    }

    if (detailMsg === 'INSUFFICIENT_LOYALTY_POINTS') {
      const value = err.error?.details?.value;
      const available = value?.available ?? 0;
      const required = value?.required ?? 0;
      const missing = Math.max(0, required - available);
      const pts = (n: number) => `${n} point${n > 1 ? 's' : ''}`;

      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Points de fidélité insuffisants : vous avez ${pts(available)} et il en faut ${pts(required)}.`,
        missing > 0 ? `Il vous manque ${pts(missing)}.` : ''
      );
      return;
    }

    if (detailMsg.includes('USAGE_LIMIT_REACHED')) {
      const parts = detailMsg.split('/');
      const promoCode = parts[1] || '';
      const message = detailMsg.includes('PER_CUSTOMER')
        ? `Vous avez atteint la limite d'utilisation du code promo "${promoCode}".`
        : `Le code promo "${promoCode}" n'est plus disponible. La limite d'utilisation globale a été atteinte.`;

      this.notificationService.sendNotification(NotificationType.WARNING, message, '');
      return;
    }

    if (detailMsg.includes('référence')) {
      this.notificationService.sendNotification(
        NotificationType.ERROR,
        `La promotion a été mise à jour. Merci de la renouveler.`,
        ''
      );
      return;
    }

    if (err.status === 404 || detailMsg.includes('/')) {
      if (detailMsg.startsWith('DeliveryCity/')) {
        this.notificationService.sendNotification(
          NotificationType.WARNING,
          `La ville de livraison n'est plus disponible. Merci de choisir une autre ville.`,
          ''
        );
        return;
      }

      const slashIndex = detailMsg.indexOf('/');
      const entityType = slashIndex !== -1 ? detailMsg.substring(0, slashIndex).toLowerCase() : '';
      const entityId = slashIndex !== -1 ? detailMsg.substring(slashIndex + 1).trim() : '';
      const foundName = this.findEntityNameInCart(entityType, entityId);

      const typeLabels: Record<string, string> = {
        promotion: 'La promotion',
        product: 'Le produit',
        bundle: 'Le menu',
        productingredient: "L'ingrédient",
        productsupplement: 'Le supplément'
      };
      const typeLabel = typeLabels[entityType] || "L'élément";

      if (foundName) {
        this.notificationService.sendNotification(
          NotificationType.WARNING,
          `${typeLabel} "${foundName}" n'est plus disponible. Merci de réviser votre sélection.`,
          ''
        );
      } else {
        this.notificationService.sendNotification(
          NotificationType.WARNING,
          `${typeLabel} n'est plus disponible. Merci de réviser votre sélection.`,
          ''
        );
      }
      return;
    }
    if (detailMsg) {
      this.notificationService.sendNotification(NotificationType.WARNING, detailMsg, '');
      return;
    }

    this.notificationService.errorHandler(err);
  }

  addToCart() {
    if (this.isSubmitting()) return;

    if (this.isPointsPromo) {
      this.addPointsPromoToCart();
      return;
    }

    if (!this.canAddToCart()) {
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        this.getValidationMessage() || 'Veuillez compléter les sélections obligatoires',
        ''
      );
      return;
    }

    this.isSubmitting.set(true); // 👈 Bloque le bouton
    const previousCartItemsSnapshot = JSON.parse(JSON.stringify(this.cartService.items()));

    if (this.editingCartItemId) {
      this.cartService.removeItem(this.editingCartItemId);
    }

    const promo = this.promoSignal();
    const buyItems = this.getSelectedBuyItems();
    const offeredItems = this.getOfferedItems();
    const formattedCustomizedItems: { [key: string]: any } = {};

    const mapItemDetails = (item: any) => {
      let totalBasePrice = 0, totalSupplementsPrice = 0;
      for (let i = 0; i < item.quantity; i++) {
        totalBasePrice += this.getProductBasePrice(item.product, item.rule);
        totalSupplementsPrice += this.getCustomizationExtraPrice(item.rule.id, item.product.id, i);
        const baseKey = this.getItemKey(item.rule.id, item.product.id);

        if (this.customizedItems[baseKey]?.[i]) {
          formattedCustomizedItems[`${baseKey}_${i}`] = JSON.parse(
            JSON.stringify(this.customizedItems[baseKey][i])
          );
        }
      }
      return {
        product: item.product,
        quantity: item.quantity,
        rule: item.rule,
        unitBasePrice: totalBasePrice / item.quantity,
        unitSupplementsPrice: totalSupplementsPrice / item.quantity,
        unitPrice: (totalBasePrice + totalSupplementsPrice) / item.quantity,
        totalBasePrice,
        totalSupplementsPrice,
        totalPrice: totalBasePrice + totalSupplementsPrice
      };
    };

    const formattedBuyItems = buyItems.map(mapItemDetails);
    const formattedOfferedItems = offeredItems.map(mapItemDetails);

    const totalBuyBasePrice = formattedBuyItems.reduce((sum, item) => sum + item.totalBasePrice, 0);
    const totalOfferedBasePrice = formattedOfferedItems.reduce((sum, item) => sum + item.totalBasePrice, 0);
    const totalSupplementsPrice = formattedBuyItems.reduce((sum, item) => sum + item.totalSupplementsPrice, 0) +
      formattedOfferedItems.reduce((sum, item) => sum + item.totalSupplementsPrice, 0);

    let promotionBaseTotal = totalBuyBasePrice;
    const reduction = promo?.payload?.reduction;

    if (reduction) {
      const value = reduction.value || 0;
      const applyReduction = (base: number): number => {
        if (reduction.type === 'PERCENTAGE') {
          const percent = Math.max(0, Math.min(1, value));
          return base * (1 - percent);
        }
        if (reduction.type === 'FIXED_AMOUNT') {
          return Math.max(0, base - value);
        }
        if (reduction.type === 'SET_PRICE') {
          return value;
        }
        return base;
      };

      if (!promo?.payload?.cheapestOffered) {
        const offeredAfterDiscount = applyReduction(totalOfferedBasePrice);
        promotionBaseTotal = totalBuyBasePrice + offeredAfterDiscount;
      } else {
        const cheapest = Math.min(totalBuyBasePrice, totalOfferedBasePrice);
        const expensive = Math.max(totalBuyBasePrice, totalOfferedBasePrice);
        const cheapestAfterDiscount = applyReduction(cheapest);
        promotionBaseTotal = expensive + cheapestAfterDiscount;
      }
    }

    const promotionTotal = promotionBaseTotal + totalSupplementsPrice;

    this.cartService.addPromotion({
      promotion: {
        ...promo,
        payload: {
          ...promo.payload,
          invertedDueToCheapestOffered: false,
          promotionBaseTotal: this.calculatePromotionBaseTotal(),
          totalSupplementsPrice: this.getTotalSupplementsPrice()
        }
      },
      promotionImage: promo.image || '',
      buyItems: formattedBuyItems,
      offeredItems: formattedOfferedItems,
      customizedItems: formattedCustomizedItems,
      total: promotionTotal
    });

    if (this.isCartSheetOpen) this.toggleCartSheet();

    const apiOrderType = this.cartService.getOrderTypeForAPI();
    this.cartService.saveCartToServer(apiOrderType).subscribe({
      next: () => {
        this.editingCartItemId = null;
        if (this.returnToCommand) {
          this.router.navigate(['/command']);
        } else {
          this.notificationService.sendNotification(
            NotificationType.SUCCESS,
            'Promotion ajoutée au panier !',
            ''
          );
          this.router.navigate(['/product']);
        }
      },
      error: (err) => {
        this.isSubmitting.set(false); // 👈 Réactive si échec
        this.cartService.restoreItems(previousCartItemsSnapshot);
        this.handleCartSaveError(err);
      }
    });
  }
  getTotalBuyItemsPriceWithoutSupplements(): number {
    let total = 0;
    this.getSelectedBuyItems().forEach(item => total += this.getProductBasePrice(item.product, item.rule) * item.quantity);
    return total;
  }

  getTotalOfferedPriceWithoutSupplements(): number {
    let total = 0;
    this.getOfferedItems().forEach(item => total += this.getProductBasePrice(item.product, item.rule) * item.quantity);
    return total;
  }

  shouldInvertCheapestOffered = (): boolean => false;

  private getProductBasePrice(product: any, rule: any): number {
    if (!product) return 0;
    const fullProduct = this.fullProductsCache[product.id];
    if (fullProduct) {
      if (rule?.sizes?.length > 0 && fullProduct.pricePerSizes?.length > 0) {
        const matchingPrice = fullProduct.pricePerSizes.find((ps: any) => rule.sizes.some((s: any) => s.name === ps.size.name));
        if (matchingPrice) return matchingPrice.price;
      }
      if (fullProduct.price > 0) return fullProduct.price;
    }
    if (rule?.sizes?.length > 0) {
      if (product.price && typeof product.price === 'object' && !Array.isArray(product.price)) {
        const priceStr = product.price[rule.sizes[0].name];
        if (priceStr !== undefined) return this.parsePrice(priceStr);
      }
      const matching = product.pricePerSizes?.find((ps: any) => rule.sizes.some((s: any) => s.name === ps.size.name));
      if (matching) return matching.price;
    }
    return product.price !== undefined && product.price !== null ? (typeof product.price === 'number' && product.price > 0 ? product.price : this.parsePrice(product.price)) : 0;
  }

  getItemBasePriceOnly = (ruleId: string, productId: string, index: number = 0): number => this.getProductBasePrice(this.promoSignal()?.payload?.rules.find((r: any) => r.id === ruleId)?.products.find((p: any) => p.id === productId), this.promoSignal()?.payload?.rules.find((r: any) => r.id === ruleId));
  getItemSupplementsPriceOnly = (ruleId: string, productId: string, index: number = 0): number => this.getCustomizationExtraPrice(ruleId, productId, index);

  getTotalSupplementsPrice(): number {
    let total = 0;
    this.getSelectedBuyItems().forEach(item => {
      for (let i = 0; i < item.quantity; i++) total += this.getItemSupplementsPriceOnly(item.rule.id, item.product.id, i);
    });
    this.getOfferedItems().forEach(item => {
      for (let i = 0; i < item.quantity; i++) total += this.getItemSupplementsPriceOnly(item.rule.id, item.product.id, i);
    });
    return total;
  }

  calculatePromotionBaseTotal(): number {
    if (this.isPointsPromo) return this.truncate2(this.getPointsEuroBase());
    const promo = this.promoSignal();
    let totalPayBaseItems = 0;
    const reduction = promo?.payload?.reduction;
    if (reduction?.type === 'SET_PRICE') {
      return this.truncate2(reduction.value);
    }
    this.getSelectedBuyItems().forEach(item => totalPayBaseItems += this.getProductBasePrice(item.product, item.rule) * item.quantity);
    let totalFreeBaseItems = 0;
    this.getOfferedItems().forEach(item => totalFreeBaseItems += this.getProductBasePrice(item.product, item.rule) * item.quantity);

    let baseTotal = totalPayBaseItems;

    if (reduction) {
      if (!promo?.payload?.cheapestOffered) {
        baseTotal = totalPayBaseItems + (reduction.type === 'PERCENTAGE' ? totalFreeBaseItems * (1 - reduction.value) : reduction.type === 'FIXED_AMOUNT' ? Math.max(0, totalFreeBaseItems - reduction.value) : reduction.value);
      } else {
        const cheapest = Math.min(totalPayBaseItems, totalFreeBaseItems);
        baseTotal = Math.max(totalPayBaseItems, totalFreeBaseItems) + (cheapest - (reduction.type === 'PERCENTAGE' ? cheapest * reduction.value : reduction.type === 'FIXED_AMOUNT' ? Math.min(reduction.value, cheapest) : reduction.value));
      }
    }
    return this.truncate2(baseTotal);
  }

  isSetPriceReduction() {
    const promo = this.promoSignal();

    return promo?.payload?.reduction?.type === 'SET_PRICE';
  }


  calculatePromotionTotal(): number {
    if (this.isPointsPromo) {
      // Calcul : Reste du prix de base (10.50 € - 80% = 2.10 €) + Suppléments sélectionnés
      return this.truncate2(this.getPointsEuroBase() + this.getTotalSupplementsPrice());
    }

    const promo = this.promoSignal();
    const reduction = promo?.payload?.reduction;

    const totalBuyBasePrice = this.getTotalBuyItemsPriceWithoutSupplements();
    const totalOfferedBasePrice = this.getTotalOfferedPriceWithoutSupplements();
    const totalSupplements = this.getTotalSupplementsPrice();

    let newOfferedTotal = totalOfferedBasePrice;

    if (reduction?.type === 'PERCENTAGE') {
      const percent = Math.max(0, Math.min(1, reduction.value || 0));
      newOfferedTotal = totalOfferedBasePrice * (1 - percent);
    }
    if (reduction?.type === 'SET_PRICE') {
      return this.truncate2((reduction.value || 0) + totalSupplements + totalBuyBasePrice);
    }
    if (reduction?.type === 'FIXED_AMOUNT') {
      newOfferedTotal = Math.max(0, totalOfferedBasePrice - (reduction.value || 0));
    }

    return this.truncate2(totalBuyBasePrice + newOfferedTotal + totalSupplements);
  }

  formatPointsTotal(): string {
    const pts = this.getPointsCost();
    const euroTotal = this.calculatePromotionTotal();

    return `${euroTotal.toFixed(2)} € (${pts} pts)`;
  }

  getReductionText(): string {
    const reduction = this.promoSignal()?.payload?.reduction;
    return reduction ? (reduction.type === 'PERCENTAGE' ? ` -${this.truncate2(reduction.value * 100)}%` : ` -${this.truncate2(reduction.value)}€`) : '';
  }

  doesItemHaveReduction(ruleId: string, productId: string): boolean {
    const promo = this.promoSignal();
    if (!promo?.payload?.reduction) return false;
    return promo.payload.cheapestOffered ? this.isProductInOfferedGroup(ruleId, productId) : !!promo.payload.rules.find((r: any) => r.id === ruleId)?.offered;
  }

  getItemDisplayPrice(ruleId: string, productId: string, index: number = 0): number {
    const basePrice = this.getItemBasePriceOnly(ruleId, productId, index);
    if (this.isPointsPromo) {
      return Math.round((basePrice * (1 - this.getRuleDiscount(ruleId)) + Number.EPSILON) * 100) / 100;
    }
    const reduction = this.promoSignal()?.payload?.reduction;
    return reduction?.type === 'PERCENTAGE' && this.doesItemHaveReduction(ruleId, productId)
      ? Math.round((basePrice * (1 - reduction.value) + Number.EPSILON) * 100) / 100
      : basePrice;
  }

  shouldShowStrikethroughPrice = (ruleId: string, productId: string): boolean =>
    this.isPointsPromo
      ? this.getRuleDiscount(ruleId) > 0
      : this.promoSignal()?.payload?.reduction?.type === 'PERCENTAGE' && this.doesItemHaveReduction(ruleId, productId);
  shouldShowGreenPrice = (ruleId: string, productId: string): boolean =>
    this.shouldShowStrikethroughPrice(ruleId, productId);
  getTotalReductionAmount = (): number => {
    if (this.isPointsPromo) {
      return this.truncate2(this.getSelectedBuyItems().reduce((sum, item) =>
        sum + this.getProductBasePrice(item.product, item.rule) * (item.rule.discount ?? 0) * item.quantity, 0));
    }
    const promo = this.promoSignal();
    if (!promo?.payload?.reduction) return 0;

    // Si c'est un SET_PRICE, la réduction globale est la différence entre le total théorique et le prix fixé
    if (promo.payload.reduction.type === 'SET_PRICE') {
      const totalTheorique = this.getTotalBuyItemsPriceWithoutSupplements() + this.getTotalOfferedPriceWithoutSupplements();
      return Math.max(0, this.truncate2(totalTheorique - promo.payload.reduction.value));
    }

    return this.truncate2(promo.payload.reduction.type === 'PERCENTAGE' ? this.getTotalOfferedPriceWithoutSupplements() * promo.payload.reduction.value : Math.min(promo.payload.reduction.value, this.getTotalOfferedPriceWithoutSupplements()));
  };
  getTotalBeforeReduction = (): number => Number(this.getTotalBuyItemsPriceWithoutSupplements() + this.getTotalOfferedPriceWithoutSupplements() + this.getTotalSupplementsPrice());
  doesBuyItemsGroupHaveReduction = (): boolean => !this.promoSignal()?.payload?.cheapestOffered && this.getSelectedBuyItems().some(item => this.doesItemHaveReduction(item.rule.id, item.product.id));
  doesOfferedItemsGroupHaveReduction = (): boolean => this.promoSignal()?.payload?.cheapestOffered ? this.getOfferedItems().length > 0 : this.getOfferedItems().some(item => this.doesItemHaveReduction(item.rule.id, item.product.id));
  getFixedAmountReductionBadge = (): string => this.promoSignal()?.payload?.reduction?.type === 'FIXED_AMOUNT' ? `-${this.promoSignal().payload.reduction.value}€` : '';
  private customerService = inject(CustomerService);

  private getPointsAlreadyInCart(): number {
    if (this.editingCartItemId) {
      const editingItem = this.cartService.getItem(this.editingCartItemId);
      const editingPoints = (editingItem?.redeemWithPoints ? (editingItem.pointsCost ?? 0) * editingItem.quantity : 0);
      return Math.max(0, this.cartService.totalPointsInCart() - editingPoints);
    }
    return this.cartService.totalPointsInCart();
  }

  private verifyPointsThen(needed: number, onOk: () => void): void {
    this.customerService.getProfile().pipe(
      map((res: any) => Number(res.body?.data?.loyaltyPoints ?? 0)),
      catchError(() => of(null)),
      takeUntil(this.destroy$)
    ).subscribe(balance => {
      if (balance == null) {
        this.isSubmitting.set(false);
        this.notificationService.sendNotification(NotificationType.ERROR, 'Connexion requise',
          'Veuillez vous connecter pour utiliser vos points de fidélité.');
        return;
      }
      const available = Math.max(balance - this.getPointsAlreadyInCart(), 0);
      if (needed > available) {
        this.isSubmitting.set(false);
        this.notificationService.sendNotification(NotificationType.ERROR, 'Solde de points insuffisant',
          `Cette offre nécessite ${needed} pts, mais vous n'avez que ${available} pts disponibles.`);
        return;
      }
      onOk();
    });
  }

  private getAllSelectedItemsFlat(): Array<{ product: any, rule: any, price: number }> {
    const rules = this.promoSignal()?.payload?.rules;
    if (!rules) return [];
    const allItems: any[] = [];
    rules.forEach((rule: any) => {
      rule.products.forEach((product: any) => {
        const qty = this.getItemQuantity(rule.id, product.id);
        for (let i = 0; i < qty; i++) allItems.push({product, rule, price: this.getProductBasePrice(product, rule)});
      });
    });
    return allItems;
  }

  private getTotalOfferedQuantity = (): number => this.promoSignal()?.payload?.rules ? this.promoSignal().payload.rules.filter((r: any) => r.offered).reduce((sum: number, r: any) => sum + r.quantity, 0) : 0;
  private isProductInOfferedGroup = (ruleId: string, productId: string): boolean => this.getOfferedItems().some(item => item.rule.id === ruleId && item.product.id === productId);
  private getFilteredAdditionsForProduct = (additions: any[], selectedSize: string | null): any[] => (!additions || additions.length === 0) ? [] : (!selectedSize ? additions : additions.filter((addition: any) => !addition.sizes || addition.sizes.length === 0 || addition.sizes.some((size: any) => size.name === selectedSize)));
  truncate2 = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100;
  getOrderTypeLabel = (): string => {
    const labels: Record<TypeOrderEnum, string> = {
      [TypeOrderEnum.DELIVERY]: 'En Livraison',
      [TypeOrderEnum.TAKEAWAY]: 'À Emporter',
      [TypeOrderEnum.ON_SITE]: 'Sur Place'
    };
    return labels[this.selectedOrderType()!] || '';
  };

  getFormattedLocation() {
    if (this.isTakeawayOrOnSite) {
      return {
        text: this.restaurantAddress() || '',
        hasAddress: !!this.restaurantAddress()
      };
    }

    const city = this.cityName() || '';
    const zip = this.zipCode();
    const text = zip ? `${city} ${zip}` : city;

    return {text, hasAddress: false};
  }

  formatDeliveryDelay(): string {
    const delay = this.deliveryDelay();
    if (!delay) return '';
    const match = delay.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
    if (!match) return '';
    const h = match[1] ? parseInt(match[1]) : 0, m = match[2] ? parseInt(match[2]) : 0;
    return h > 0 && m > 0 ? `${h}h ${m}min` : h > 0 ? `${h}h` : `${m}min`;
  }

  getMinOrderAmount(): string {
    const amounts = this.minOrderAmounts();
    if (!amounts || amounts.length === 0) return '';
    const currentSeconds = (() => {
      const [h, m, s] = new Date().toTimeString().split(' ')[0].split(':').map(Number);
      return h * 3600 + m * 60 + s;
    })();
    const toSec = (t: string) => {
      const [h, m, s] = t.split(':').map(Number);
      return h * 3600 + m * 60 + s;
    };
    for (const slot of amounts) {
      const from = toSec(slot.from), to = toSec(slot.to);
      if ((from <= to && currentSeconds >= from && currentSeconds <= to) || (from > to && (currentSeconds >= from || currentSeconds <= to))) return slot.amount === 0 ? '' : `Min Cmd: ${slot.amount.toFixed(2)}€`;
    }
    return amounts[0].amount === 0 ? '' : `Min Cmd: ${amounts[0].amount.toFixed(2)}€`;
  }

  private setSeoTags() {
    const promo = this.promoSignal() as any;
    if (!promo) return;

    this.route.title.subscribe(title => {
      this.titleService.setTitle(`${title} - ${promo.title}`)
    });

    const description = this.generatePromoDescription();
    const keywords = this.extractPromoKeywords();

    this.addTag({name: 'title', content: `${promo.title} | ${this.configStore.name()}`});
    this.addTag({name: 'description', content: description});
    this.addTag({name: 'keywords', content: keywords});
    this.addTag({name: 'promotion:type', content: promo.type});
    this.addTag({name: 'promotion:discount', content: this.getDiscountValue()});
    this.addTag({name: 'promotion:availability', content: this.isPromoValid() ? 'disponible' : 'expirée'});

    this.addTag({property: 'og:title', content: promo.title});
    this.addTag({property: 'og:description', content: description.substring(0, 300)});
    this.addTag({property: 'og:image', content: promo.payload?.image || this.getDefaultPromoImage()});
    this.addTag({property: 'og:image:alt', content: promo.title});
    this.addTag({property: 'og:type', content: 'product.offer'});
    this.addTag({property: 'og:price:currency', content: '€'});

    this.addTag({name: 'twitter:card', content: 'summary_large_image'});
    this.addTag({name: 'twitter:title', content: promo.title});
    this.addTag({name: 'twitter:description', content: description.substring(0, 200)});
    this.addTag({name: 'twitter:image', content: promo.payload?.image || this.getDefaultPromoImage()});
  }

  private generatePromoDescription(): string {
    const promo = this.promoSignal() as any;
    if (!promo) return "";
    if (promo?.payload?.description) {
      return promo.payload.description;
    }

    const parts = [];
    const reduction = promo.payload?.reduction;

    // Add promotion type and discount
    if (promo.type === 'product-offer') {
      if (reduction?.type === 'PERCENTAGE') {
        const discountPercent = (reduction.value * 100).toFixed(0);
        parts.push(`${discountPercent}% de réduction sur une sélection de produits`);
      } else if (reduction?.type === 'FIXED_AMOUNT') {
        parts.push(`${reduction.value}€ de réduction sur une sélection de produits`);
      }
    }

    // Add BOGO (Buy One Get One) information
    const rules = promo.payload.rules || [];
    const boughtItems = rules.filter((r: any) => !r.offered);
    const offeredItems = rules.filter((r: any) => r.offered);

    if (boughtItems.length > 0 && offeredItems.length > 0) {
      const boughtCategories = boughtItems.map((r: any) => r.category?.name).filter(Boolean);
      const offeredCategories = offeredItems.map((r: any) => r.category?.name).filter(Boolean);

      if (boughtCategories.length && offeredCategories.length) {
        if (boughtCategories[0] === offeredCategories[0]) {
          parts.push(`Achetez ${boughtCategories.join(', ')} et bénéficiez d'un article offert ${promo.payload?.cheapestOffered ? '(le moins cher offert)' : 'gratuit'}`);
        } else {
          parts.push(`Achetez ${boughtCategories.join(', ')} et recevez ${offeredCategories.join(', ')} offert${promo.payload?.cheapestOffered ? ' (le moins cher offert)' : ''}`);
        }
      }
    }

    // Add activation conditions
    const activatedFor = promo.payload?.activatedFor || [];
    if (activatedFor.length) {
      const conditions = activatedFor.map((a: string) => {
        switch (a) {
          case 'ALL_CLIENTS':
            return 'tous les clients';
          case 'NEW_CLIENT':
            return 'nouveaux clients';
          case 'EXISTING_CLIENT':
            return 'clients existants';
          default:
            return a.replace('_', ' ').toLowerCase();
        }
      });
      parts.push(`Valable pour : ${conditions.join(', ')}`);
    }

    // Add order types
    const orderTypes = promo.payload?.orderTypes || [];
    if (orderTypes.length) {
      const types = orderTypes.map((t: string) => {
        switch (t) {
          case 'ON_SITE':
            return 'sur place';
          case 'TAKEAWAY':
            return 'à emporter';
          case 'DELIVERY':
            return 'livraison';
          default:
            return t.replace('_', ' ').toLowerCase();
        }
      });
      parts.push(`Disponible en : ${types.join(', ')}`);
    }

    // Add expiration
    if (promo.expiresAt) {
      const expiryDate = new Date(promo.expiresAt);
      parts.push(`Valable jusqu'au ${expiryDate.toLocaleDateString('fr-FR')}`);
    }

    return parts.join('. ') || promo.title;
  }

  private extractPromoKeywords(): string {
    const promo = this.promoSignal() as any;
    const keywords = [promo.title];

    // Add category names from rules
    const rules = promo.payload?.rules || [];
    rules.forEach((rule: any) => {
      if (rule.category?.name) {
        keywords.push(rule.category.name);
      }
      // Add product names if available
      rule.products?.forEach((product: any) => {
        if (product.name) {
          keywords.push(product.name);
        }
      });
    });

    // Add promotion type in French
    if (promo.type) {
      if (promo.type === 'product-offer') {
        keywords.push('offre produit');
        keywords.push('promotion');
      }
      keywords.push(promo.type.replace('-', ' '));
    }

    // Add discount type in French
    if (promo.payload?.reduction?.type) {
      const reductionType = promo.payload.reduction.type;
      if (reductionType === 'PERCENTAGE') {
        keywords.push('pourcentage');
        keywords.push('réduction');
      } else if (reductionType === 'FIXED_AMOUNT') {
        keywords.push('montant fixe');
        keywords.push('réduction');
      }
      keywords.push('promotion');
      keywords.push('offre');
    }

    // Add BOGO keywords in French
    const hasBought = promo.payload?.rules?.some((r: any) => !r.offered);
    const hasOffered = promo.payload?.rules?.some((r: any) => r.offered);
    if (hasBought && hasOffered) {
      keywords.push('achaté un offert');
      keywords.push('bogo');
      keywords.push('offre spéciale');
      keywords.push('gratuit');
    }

    // Add activation condition keywords
    const activatedFor = promo.payload?.activatedFor || [];
    activatedFor.forEach((condition: string) => {
      switch (condition) {
        case 'NEW_CLIENT':
          keywords.push('nouveaux clients');
          break;
        case 'EXISTING_CLIENT':
          keywords.push('clients fidèles');
          break;
        case 'ALL_CLIENTS':
          keywords.push('tous clients');
          break;
      }
    });

    // Add order type keywords
    const orderTypes = promo.payload?.orderTypes || [];
    orderTypes.forEach((type: string) => {
      switch (type) {
        case 'ON_SITE':
          keywords.push('sur place');
          break;
        case 'TAKEAWAY':
          keywords.push('à emporter');
          break;
        case 'DELIVERY':
          keywords.push('livraison');
          break;
      }
    });

    // Remove duplicates and limit
    const uniqueKeywords = [...new Set(keywords)];
    return uniqueKeywords.slice(0, 15).join(', ');
  }

  private getDiscountValue(): string {
    const promo = this.promoSignal() as any;
    const reduction = promo.payload?.reduction;
    if (!reduction) return '0';

    if (reduction.type === 'PERCENTAGE') {
      return `${(reduction.value * 100).toFixed(0)}%`;
    } else if (reduction.type === 'FIXED_AMOUNT') {
      return `${reduction.value}€`;
    }
    return '0';
  }

  private isPromoValid(): boolean {
    const promo = this.promoSignal() as any;
    if (!promo.expiresAt) return true;
    const expiryDate = new Date(promo.expiresAt);
    const now = new Date();
    return expiryDate > now;
  }

  private getDefaultPromoImage(): string {
    // Return a default promotion image or placeholder
    return '/assets/images/promotion-placeholder.jpg';
  }

  private addTag(tag: MetaDefinition, forceCreation?: boolean) {
    const t = this.metaService.addTag(tag, forceCreation)
    if (t) this.metaTags.push(t)
  }

  private removeSeoTags() {
    this.metaTags.forEach(tag => this.metaService.removeTagElement(tag))
    console.log(`Cleared ${this.metaTags.length} tags`)
    this.metaTags = []
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.removeSeoTags();
    this.hideFloatingCartService.setHide(false);

    // Réinitialiser le scroll du body au cas où le composant est détruit pendant que la cartSheet est ouverte
    if (this.isBrowser) {
      document.body.style.overflow = '';
    }

    if (this.isBrowser && !this.router.url.includes('/product')) {
      sessionStorage.removeItem('productPageState');
    }
  }

  trackItem(item: string, index: number) {
    return `${item}-${index}`;
  }
}
